import { BASE_URL } from '@dreamstack/feature-components/src/config/config'
import { useComingSoonBaseUrl } from '@dreamstack/feature-components/src/projects/helper/useComingSoonBaseUrl'
import { useMinMaxOfProperties } from '@dreamstack/feature-components/src/projects/helper/getMinMaxOfProperties'
import { useProjectBaseUrl } from '@dreamstack/feature-components/src/projects/helper/useProjectBaseUrl'
import type { EquipmentFeature } from '@dreamstack/feature-components/src/property/EquipmentFeatures'
import type { FullProject } from '@dreamstack/web/lib/prisma/projects'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import filter from 'lodash/filter'
import first from 'lodash/first'
import map from 'lodash/map'
import { ProductJsonLd } from 'next-seo'
import type { FunctionComponent } from 'react'
import {
  getItemAvailabilityForStatus,
  MARKUP_IMAGE_UPPER_BOUND,
} from './MarkupUtil'

export const ProjectRichMarkup: FunctionComponent<React.PropsWithChildren<{
  project: FullProject
  properties: PropertyPreview[]
}>> = ({ project, properties }) => {
  const projectBaseUrl = useProjectBaseUrl()
  const comingSoonBaseUrl = useComingSoonBaseUrl()
  const { minPriceRaw, maxPriceRaw } = useMinMaxOfProperties({
    properties,
  })

  // FIXME: Why are we only using information from the first property?
  const fistProperty = first(properties)
  if (!fistProperty) return null

  const name = project.title?.split('–')[0]?.trim() ?? ''
  const description = project.description_note ?? ''
  const imageUrls = map(project.project_images, (images) => images.url).slice(
    0,
    MARKUP_IMAGE_UPPER_BOUND
  )

  const filteredHref = project.markedAsComingSoon
    ? `${BASE_URL}${comingSoonBaseUrl}/${project.id}`
    : `${BASE_URL}${projectBaseUrl}/${project.id}`
  const url = filteredHref

  const addressLocality = project.city ?? ''
  const addressRegion = project.region ?? ''
  const postalCode = project.zip_code ?? ''
  const streetAddress = `${project.street ?? ''} ${
    project.house_number ?? ''
  }`.trim()

  const numberOfAvailableAccommodationUnits = project.properties.length

  const floorLevel = fistProperty.floor_position
  let equipmentFeatures: EquipmentFeature[] =
    fistProperty.equipment_features || []

  equipmentFeatures = filter(equipmentFeatures, (f) => !!f.value && !!f.label)
  const amenityFeatures = map(equipmentFeatures, (features) => features.label)

  const geoCoordinates = fistProperty.geoLocation

  const floorSize = map(properties, (p) => p.property_space_value)

  const offers = map(properties, (property) => {
    const availability = getItemAvailabilityForStatus(property.status ?? 0)
    return {
      price: property.price,
      priceCurrency: 'EUR',
      availability: availability,
      url: url,
      seller: { name: 'Accentro GmbH' },
    }
  })

  return (
    <ProductJsonLd
      productName={name}
      brand="Accentro GmbH"
      description={description}
      images={imageUrls}
      aggregateOffer={{
        priceCurrency: 'EUR',
        lowPrice: minPriceRaw,
        highPrice: maxPriceRaw,
        offerCount: properties?.length,
        offers: offers,
        //address is not supported in AggregateOffer schema. leaving it here so it is consistent with the snippets provided by Martijn
        // TODO: Determine what fields should be set. Regarding Martijn there are made changes by google regualrily,
        // what fields are accepted. So he suggested to just leave some of the not accepted ones in.
        address: {
          type: 'PostalAddress',
          // Copy pasted from Martijn's snippet. Hardcoded 'DE' for now.
          addressCountry: 'DE',
          addressLocality: addressLocality,
          addressRegion: addressRegion,
          postalCode: postalCode,
          streetAddress: streetAddress,
          numberOfAvailableAccommodationUnits:
            numberOfAvailableAccommodationUnits,
          floorLevel: floorLevel,
          amenityFeature: amenityFeatures,
          geo: geoCoordinates,
          //it is also not part of the PostalAddress schema
          floorSize: {
            type: 'QuantitativeValue',
            value: [floorSize],
            unitCode: 'MTK',
          },
        },
      }}
    />
  )
}
